import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container } from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import ActionButton from '../../Components/Common/ActionButton';
import { DetailBackButton } from '../../Components/Common/Detail';
import StatusBadge from '../../Components/Common/StatusBadge';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import { CheckField, TextAreaField } from '../../Components/Form';
import GtcViewer, { NO_AMEND_TEXT } from '../../Components/Gtc/GtcViewer';
import { formatAmendDate } from '../../Components/Gtc/gtcAmendSummary';
import notify from '../../utils/notify';
import { confirmAction, confirmSubmit } from '../../utils/confirm';
import '../TenderOpening/components/TenderOpening.css';
import './components/GtcView.css';
import { PUBLISH_TONE, gtcApi, monthName, publishLabel } from './data/gtcApi';
import { AMEND_TONE } from './GtcAmendView';
import {
  downloadGtcAmendDocx,
  downloadGtcAmendPdf,
} from './utils/gtcAmendDocument';

const LIST_PATH = '/apps-gtc-amend/view';

// Apply unsaved drafts (clauseId -> {isAmend, amendDetail}) onto the tree.
const mergeDrafts = (gtc, drafts) =>
  gtc && {
    ...gtc,
    categories: (gtc.categories || []).map(category => ({
      ...category,
      articles: (category.articles || []).map(article => ({
        ...article,
        clauses: (article.clauses || []).map(clause =>
          drafts[clause.id] ? { ...clause, ...drafts[clause.id] } : clause
        ),
      })),
    })),
  };

const GtcAmendDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gtc, setGtc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [denied, setDenied] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  document.title = `Master Amend GTC - ${gtc?.gtcNumber || id}`;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await gtcApi.getAmend(id);
      setGtc(data || null);
      setNotFound(!data);
      setDrafts({});
      setErrors({});
    } catch (err) {
      const code = err?.response?.status ?? err?.status;
      if (code === 404) setNotFound(true);
      else {
        if (code === 403) setDenied(true);
        notify.apiError(err, 'load GTC');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const merged = useMemo(() => mergeDrafts(gtc, drafts), [gtc, drafts]);
  const canAmend = Boolean(gtc?.canAmend);
  const dirtyIds = Object.keys(drafts);
  const isDirty = dirtyIds.length > 0;

  // Browser close/reload with unsaved amendments.
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = e => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const setDraft = (clause, patch) => {
    setDrafts(prev => ({
      ...prev,
      [clause.id]: {
        isAmend: Boolean(clause.isAmend),
        amendDetail: clause.amendDetail || '',
        ...prev[clause.id],
        ...patch,
      },
    }));
    setErrors(prev => ({ ...prev, [clause.id]: '' }));
  };

  // Returns the PUT items, or null after flagging missing details.
  const collect = ids => {
    const nextErrors = {};
    const items = ids.map(clauseId => {
      const d = drafts[clauseId];
      if (d.isAmend && !String(d.amendDetail || '').trim())
        nextErrors[clauseId] = 'Supplier Proposed Amendment is required.';
      return {
        gtcClauseId: clauseId,
        isAmend: d.isAmend,
        amendDetail: d.isAmend ? String(d.amendDetail).trim() : null,
      };
    });
    if (Object.keys(nextErrors).length) {
      setErrors(prev => ({ ...prev, ...nextErrors }));
      notify.warning('Please fill in the amendment detail.');
      return null;
    }
    return items;
  };

  const save = async ids => {
    const items = collect(ids);
    if (!items) return;
    setSaving(true);
    try {
      // Only the saved clauses' drafts are cleared; others stay as edited.
      const fresh =
        (await gtcApi.saveAmend(id, items)) || (await gtcApi.getAmend(id));
      if (fresh) setGtc(fresh);
      setDrafts(prev => {
        const next = { ...prev };
        ids.forEach(clauseId => delete next[clauseId]);
        return next;
      });
      notify.success('GTC amendment saved.');
    } catch (err) {
      notify.apiError(err, 'save GTC amendment');
    } finally {
      setSaving(false);
    }
  };

  const saveAll = async () => {
    if (!dirtyIds.length) {
      notify.info('No changes to save.');
      return;
    }
    const ok = await confirmSubmit('GTC amendment', gtc.gtcNumber, {
      title: 'Save GTC amendment information?',
      message: `${dirtyIds.length} changed clause(s) will be saved.`,
      confirmLabel: 'Save GTC Amendment',
    });
    if (ok) save(dirtyIds);
  };

  const goBack = async () => {
    if (dirtyIds.length) {
      const ok = await confirmAction({
        tone: 'warning',
        title: 'Discard changes?',
        message: 'Unsaved amendments will be lost.',
        confirmLabel: 'Discard changes',
      });
      if (!ok) return;
    }
    navigate(LIST_PATH);
  };

  const download = async fn => {
    try {
      await fn(gtc);
    } catch (err) {
      notify.apiError(err, 'download document');
    }
  };

  const renderAmend = clause => {
    const dirty = Boolean(drafts[clause.id]);
    return (
      <div>
        <CheckField
          name={`isAmend-${clause.id}`}
          switchStyle
          label={`Is Amend: ${clause.isAmend ? 'ON' : 'OFF'}`}
          checked={Boolean(clause.isAmend)}
          mode={saving ? 'disabled' : 'editable'}
          onChange={e => setDraft(clause, { isAmend: e.target.checked })}
          className='mb-2'
        />
        {clause.isAmend ? (
          <>
            <TextAreaField
              label='Amendment Detail Information (Supplier Proposed Amendment)'
              name={`amendDetail-${clause.id}`}
              required
              rows={3}
              value={clause.amendDetail || ''}
              mode={saving ? 'disabled' : 'editable'}
              error={errors[clause.id]}
              onChange={e => setDraft(clause, { amendDetail: e.target.value })}
            />
            <div className='d-flex align-items-center gap-2 flex-wrap'>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                disabled={!dirty || saving}
                onClick={() => save([clause.id])}
              >
                Save Amendment
              </ActionButton>
              {!dirty && clause.amendedAt && (
                <span className='gtcv-saved mt-0'>
                  <i className='ri-check-line' aria-hidden='true' /> Amendment
                  Saved ({formatAmendDate(clause.amendedAt)})
                </span>
              )}
            </div>
          </>
        ) : (
          <>
            <div className='gtcv-text text-muted'>{NO_AMEND_TEXT}</div>
            {dirty && (
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                disabled={saving}
                onClick={() => save([clause.id])}
                className='mt-2'
              >
                Save Amendment
              </ActionButton>
            )}
          </>
        )}
      </div>
    );
  };

  const amendLabel = gtc?.amendStatus || 'Not Amended';
  const pubLabel = publishLabel(gtc);

  let body;
  if (loading && !gtc) {
    body = (
      <div className='to-panel'>
        <SkeletonRows count={4} />
      </div>
    );
  } else if (denied) {
    body = (
      <div className='to-panel'>
        <EmptyState
          icon='ri-lock-line'
          title='You do not have access'
          emptyHint='This account does not have access to Master Amend GTC.'
          action={{
            label: 'Back to Master Amend GTC List',
            onClick: () => navigate(LIST_PATH),
          }}
        />
      </div>
    );
  } else if (notFound || !gtc) {
    body = (
      <div className='to-panel'>
        <EmptyState
          title='GTC not found'
          emptyHint='This GTC is not available for amendment.'
          action={{
            label: 'Back to Master Amend GTC List',
            onClick: () => navigate(LIST_PATH),
          }}
        />
      </div>
    );
  } else {
    body = (
      <div className='gtc-stack'>
        {!canAmend && (
          <div className='alert alert-info py-2 mb-0'>
            <i className='ri-eye-line' aria-hidden='true' /> View only — this
            account is not linked to a Registered Supplier.
          </div>
        )}
        <div className='to-panel p-3'>
          <GtcViewer
            gtcs={[merged]}
            showAmend
            renderAmend={canAmend ? renderAmend : undefined}
          />
        </div>
        {canAmend && (
          <div className='d-flex justify-content-end'>
            <ActionButton
              variant='primary'
              icon='ri-save-3-line'
              disabled={saving || !dirtyIds.length}
              onClick={saveAll}
            >
              Save GTC Amendment Information
            </ActionButton>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className='page-content'>
      <Container fluid>
        <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
          <div className='email-content minimal-border'>
            <div className='p-4'>
              <div className='to-hero mb-4'>
                <PageBreadcrumb
                  current={gtc?.gtcNumber || id}
                  fallback={{ label: 'Master Amend GTC', to: LIST_PATH }}
                />
                <div className='dt-header__top'>
                  <DetailBackButton
                    onDark
                    label='Back to Master Amend GTC List'
                    onClick={goBack}
                  />
                  {gtc && (
                    <>
                      <StatusBadge
                        label={amendLabel}
                        tone={AMEND_TONE[amendLabel]}
                        size='sm'
                      />
                      <StatusBadge
                        label={pubLabel}
                        tone={PUBLISH_TONE[pubLabel]}
                        size='sm'
                      />
                      <span className='gtc-hero-meta__note'>
                        Amended Clauses: {gtc.amendedClauseCount ?? 0}
                      </span>
                    </>
                  )}
                </div>
                <div className='d-flex align-items-center gap-3 flex-wrap'>
                  <span className='to-hero-icon'>
                    <i className='ri-file-edit-line' aria-hidden='true' />
                  </span>
                  <div className='flex-grow-1'>
                    <h4 className='to-hero-title'>
                      {gtc ? `${gtc.gtcNumber} — ${gtc.title}` : 'GTC'}
                    </h4>
                    <p className='to-hero-subtitle'>
                      Review Category (Part), Article, and Clause hierarchy —
                      toggle &quot;Is Amend&quot; to propose clause amendments
                      {gtc && ` · ${monthName(gtc.month)} ${gtc.year}`}
                    </p>
                  </div>
                  <div className='d-flex gap-2 flex-wrap'>
                    {gtc && (
                      <>
                        <button
                          type='button'
                          className='to-hero-btn'
                          onClick={() => download(downloadGtcAmendDocx)}
                        >
                          <i className='ri-file-word-line' aria-hidden='true' />
                          Download Word
                        </button>
                        <button
                          type='button'
                          className='to-hero-btn'
                          onClick={() => download(downloadGtcAmendPdf)}
                        >
                          <i className='ri-file-pdf-line' aria-hidden='true' />
                          Download PDF
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              {body}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default GtcAmendDetailView;
